import { useMemo } from "react";
/**
 * Calculate total basket weight in kilograms
 * Product weights are stored in grams, so we need to convert to kg
 * Starshipit API expects weight in kilograms
 */
function useTotalBasketWeight(products) {
    return useMemo(() => {
        if (!products || products.length === 0) {
            return 1; // Default to 1kg if no products
        }
        const totalWeightInGrams = products.reduce((total, product) => {
            // Find weight attribute
            const weightAttr = product.attributes?.find((attr) => attr.description?.toLowerCase().trim().replace(/,/g, "") === "weight");
            // Find weight units attribute
            const weightUnitAttr = product.attributes?.find((attr) => attr.description?.toLowerCase().trim().replace(/,/g, "") === "weightunits");
            const weightValue = weightAttr?.default_value ?? 0;
            const weightUnit = weightUnitAttr?.default_value?.toString().toLowerCase().trim().replace(/,/g, "") || "g";
            const quantity = parseInt(String(product.quantity || "1"), 10);
            // Convert to grams first
            let weightInGrams = 0;
            switch (weightUnit) {
                case "g":
                case "gram":
                case "grams":
                    weightInGrams = parseFloat(weightValue.toString());
                    break;
                case "kg":
                case "kilogram":
                case "kilograms":
                    weightInGrams = parseFloat(weightValue.toString()) * 1000; // Convert kg to grams
                    break;
                case "oz":
                case "ounce":
                case "ounces":
                    weightInGrams = parseFloat(weightValue.toString()) * 28.3495; // Convert oz to grams
                    break;
                case "lb":
                case "lbs":
                case "pound":
                case "pounds":
                    weightInGrams = parseFloat(weightValue.toString()) * 453.592; // Convert lb to grams
                    break;
                default:
                    // Assume grams if unit is unknown
                    weightInGrams = parseFloat(weightValue.toString());
                    break;
            }
            return total + weightInGrams * quantity;
        }, 0);
        // Convert total from grams to kilograms (Starshipit expects kg)
        const totalWeightInKg = totalWeightInGrams / 1000;
        // Ensure minimum 0.001kg (1 gram) to avoid zero weight
        return Math.max(totalWeightInKg, 0.001);
    }, [products]);
}
export default useTotalBasketWeight;
